import moment, { Moment } from "moment";
import { useEffect, useState } from "react";

// dateFormat is how a date is written everywhere it crosses the wire or a url
export const dateFormat = "YYYY-MM-DD";

// shortDate labels a date for somewhere with no room to spell it out. The year is
// left off while it's the year being read, which is most of the time.
export function shortDate(value: string) {
    const date = moment(value, dateFormat);
    return date.format(date.isSame(moment(), "year") ? "MMM D" : "MMM D, YYYY");
}

// dateOf is the utc day a timestamp falls on
export function dateOf(value: string) {
    return moment.utc(value).format(dateFormat);
}

export function isToday(date: Moment) {
    return date.isSame(moment(), "day");
}

// useToday is the date the reader is on, re-read when the clock passes midnight so a
// page left open overnight stops calling yesterday today
export function useToday() {
    const [today, setToday] = useState(() => moment().format(dateFormat));

    useEffect(() => {
        let timer = 0;
        const read = () => setToday(moment().format(dateFormat));

        // wake just after the next midnight rather than polling for it
        const schedule = () => {
            const now = moment();
            const wait = now.clone().add(1, "day").startOf("day").diff(now) + 1000;

            timer = window.setTimeout(() => {
                read();
                schedule();
            }, wait);
        };

        // a background tab has its timers throttled and a sleeping machine runs none
        // at all, so coming back to the page is the other way the date gets re-read
        const wake = () => {
            if (document.hidden) return;

            read();
            window.clearTimeout(timer);
            schedule();
        };

        schedule();
        window.addEventListener("focus", wake);
        document.addEventListener("visibilitychange", wake);

        return () => {
            window.clearTimeout(timer);
            window.removeEventListener("focus", wake);
            document.removeEventListener("visibilitychange", wake);
        };
    }, []);

    return today;
}
